import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { callErrorToast } from "../../../utilities/utilities.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import {
  KpiShipmentStatisticAggregationDaily,
  KpiShipmentStatisticFilter,
} from "../../../Models/KpiShipmentStatistic.ts";
import { KpiService } from "../../../services/shipment/kpi.ts";

const kpiService = new KpiService();

interface ShipmentsStatisticsAggregationDailyProviderProps {
  children: ReactNode;
  filters: KpiShipmentStatisticFilter;
}

interface ShipmentsStatisticsAggregationDailyContextValue {
  data: KpiShipmentStatisticAggregationDaily[] | null;
  loader: boolean;
  filters: KpiShipmentStatisticFilter;
}

const ShipmentsStatisticsAggregationDailyContext = createContext<
  ShipmentsStatisticsAggregationDailyContextValue | undefined
>(undefined);

const ShipmentsStatisticsAggregationDailyProvider: React.FC<
  ShipmentsStatisticsAggregationDailyProviderProps
> = ({ children, filters }) => {
  const [data, setData] = useState<
    KpiShipmentStatisticAggregationDaily[] | null
  >(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] =
    useState<ErrorResponse<KpiShipmentStatisticAggregationDaily> | null>(null);

  const prevFilters = useRef<KpiShipmentStatisticFilter>(filters);

  const getStatisticsAggregationDaily = (): void => {
    setLoader(true);
    kpiService
      .getStatisticsAggregationDaily(filters)
      .then((res) => {
        setLoader(false);
        setData(res?.data);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });

    return;
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      filters &&
      JSON.stringify(prevFilters.current) !== JSON.stringify(filters)
    ) {
      getStatisticsAggregationDaily();
      prevFilters.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    getStatisticsAggregationDaily();
  }, []);

  return (
    <ShipmentsStatisticsAggregationDailyContext.Provider
      value={{
        data,
        loader,
        filters,
      }}
    >
      {children}
    </ShipmentsStatisticsAggregationDailyContext.Provider>
  );
};

/**
 * Custom hook to access the ShipmentsStatisticsAggregationDailyContext.
 * Ensures that the context is used only within a ShipmentsStatisticsAggregationDailyProvider.
 */
const useShipmentsStatisticsAggregationDailyContext =
  (): ShipmentsStatisticsAggregationDailyContextValue => {
    const context = useContext(ShipmentsStatisticsAggregationDailyContext);
    if (!context) {
      throw new Error(
        "useShipmentsStatisticsAggregationDailyContext must be used within a ShipmentsStatisticsAggregationDailyProvider"
      );
    }
    return context;
  };

// Export the provider and custom hook for use in other components
export {
  ShipmentsStatisticsAggregationDailyProvider,
  useShipmentsStatisticsAggregationDailyContext,
};
